import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { CircleQuestionMark } from '@repo/media/icons';

import { ButtonLink } from '@repo/components/button-link';
import { Card } from '@repo/components/card';
import { Heading } from '@repo/components/heading';
import { LoadingIndicator } from '@repo/components/loading-indicator';

import type { Route as AppRoute } from '~/config/routes.ts';
import { routesByPath } from '~/config/routes.ts';
import { FeatureGuard } from '~/features/auth/feature-guard.tsx';
import { useFeatureGuard } from '~/features/auth/use-feature-guard.ts';
import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { queryProjectById } from '~/services/authorization/authorization.ts';

type NavCategory = {
  title: string;
  routes: AppRoute[];
};

const categories: NavCategory[] = [
  {
    title: 'Operations',
    routes: [
      routesByPath['/project/$project/cargoes'],
      routesByPath['/project/$project/declarations'],
      routesByPath['/project/$project/certificates'],
      routesByPath['/project/$project/invoices'],
    ],
  },
  {
    title: 'Administration',
    routes: [
      routesByPath['/project/$project/user-mgmt'],
    ],
  },
];

export const Route = createFileRoute('/project/$project/')({
  loader: async ({ context: { queryClient }, params: { project } }) => {
    return queryClient.ensureQueryData(queryProjectById(project));
  },
  component: ProjectDashboard,
});

function ProjectDashboard() {
  const projectId = Route.useParams().project;
  const project = useSuspenseQuery(queryProjectById(projectId)).data;
  const { hasSomeFeature, isLoading: isFeatureGuardLoading } = useFeatureGuard();
  const visibleCategories = categories
    .map(category => ({
      ...category,
      routes: category.routes.filter(route => hasSomeFeature(route.features)),
    }))
    .filter(category => category.routes.length > 0);

  return (
    <div className="@container flex flex-col items-start gap-8 pb-6">
      <div className="flex flex-col gap-2">
        <ContentHeader>
          {project.name}
        </ContentHeader>
      </div>

      {isFeatureGuardLoading
        ? (
            <div className="w-full min-h-32 flex items-center justify-center">
              <LoadingIndicator size={24} className="text-primary-700" />
            </div>
          )
        : (
            <div className="grid @4xl:grid-cols-2 gap-6 w-full">
              {visibleCategories.map(category => (
                <Card key={category.title}>
                  <Heading className="font-semibold text-base">{category.title}</Heading>
                  <div className="flex flex-col gap-2">
                    {category.routes.map(({ icon: Icon = CircleQuestionMark, ...route }) => (
                      <FeatureGuard key={route.path} features={route.features}>
                        <ButtonLink
                          to={route.path}
                          params={{ project: projectId }}
                          icon={{ component: <Icon size={20} strokeWidth={1.5} aria-label={route.title} /> }}
                          variant="ghost"
                          color="secondary"
                          size="sm"
                          className="justify-start"
                        >
                          {route.title}
                        </ButtonLink>
                      </FeatureGuard>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
    </div>
  );
}
